import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarCheck, VideoCamera, CurrencyInr } from "@phosphor-icons/react";

export default function Appointments() {
  const [apts, setApts] = useState([]);
  const nav = useNavigate();

  const load = () => api.get("/appointments/mine").then((r) => setApts(r.data));
  useEffect(() => { load(); }, []);

  const pay = async (id) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        appointment_id: id, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Payment could not be initiated");
    }
  };

  return (
    <div className="p-6 md:p-12 pb-24 md:pb-12">
      <div className="mb-8">
        <div className="overline text-terracotta">My schedule</div>
        <h1 className="font-heritage text-5xl text-ayurveda mt-1">Appointments</h1>
      </div>

      {apts.length === 0 && (
        <div className="rounded-2xl bg-white p-12 text-center border border-black/5">
          <CalendarCheck size={48} weight="duotone" className="mx-auto text-ayurveda/40 mb-4" />
          <div className="font-clinical text-lg text-ayurveda">No appointments yet</div>
          <p className="text-sm text-charcoal/60 mt-1">Book with a verified doctor from the Doctors page.</p>
        </div>
      )}

      <div className="space-y-3">
        {apts.map((a) => {
          const dt = new Date(a.slot_iso);
          return (
            <Card key={a.id} className="border-black/5 bg-white" data-testid={`apt-${a.id}`}>
              <CardContent className="p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className="bg-ayurveda text-bone">{a.specialty}</Badge>
                    <Badge variant="outline" className="capitalize">{a.mode}</Badge>
                    {a.payment_status === "paid" ? (
                      <Badge className="bg-green-700 text-white">Paid</Badge>
                    ) : (
                      <Badge className="bg-terracotta text-white">Payment pending</Badge>
                    )}
                  </div>
                  <div className="font-clinical text-lg text-ayurveda">{a.doctor_name}</div>
                  <div className="text-sm text-charcoal/70 mt-1">
                    {dt.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })} at {dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                  {a.notes && <div className="text-xs text-charcoal/60 mt-2 italic">"{a.notes}"</div>}
                </div>
                <div className="flex items-center gap-2">
                  {a.payment_status !== "paid" && (
                    <Button onClick={() => pay(a.id)} className="bg-terracotta hover:bg-terracotta/90 text-white" data-testid={`pay-${a.id}`}>
                      <CurrencyInr size={16} className="mr-1" /> Pay ₹{a.fee}
                    </Button>
                  )}
                  <Button variant="outline" data-testid={`join-${a.id}`} onClick={() => nav(`/video/${a.id}`)}>
                    <VideoCamera size={16} className="mr-2" /> Join
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
